import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '@/lib/api'
import { formatPhoneDisplay } from '@/lib/utils'
import { formatDistanceToNow, format } from 'date-fns'
import {
  Users, Search, Phone, Mail, MessageSquare, Globe, PhoneForwarded, PhoneIncoming,
  Loader2,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useToast } from '@/hooks/use-toast'
import { PageHeader, EmptyState } from '@/components/dashboard-ui'

type LeadSource = 'inbound_call' | 'outbound_call' | 'chat' | 'web_form'
type LeadStatus = 'new' | 'contacted' | 'booked' | 'qualified' | 'won' | 'lost'

interface Lead {
  id: string
  name: string | null
  email: string | null
  phone: string | null
  caseType: string | null
  leadScore: number | null
  source: LeadSource
  status: LeadStatus
  firstSeenAt: string
  lastActivityAt: string
}

const SOURCE_META: Record<LeadSource, {
  label: string
  icon: typeof Phone
  iconClass: string
  bgClass: string
}> = {
  inbound_call: {
    label: 'Inbound call',
    icon: PhoneIncoming,
    iconClass: 'text-emerald-600 dark:text-emerald-400',
    bgClass: 'bg-emerald-100 dark:bg-emerald-900/30',
  },
  outbound_call: {
    label: 'Outbound call',
    icon: PhoneForwarded,
    iconClass: 'text-sky-600 dark:text-sky-400',
    bgClass: 'bg-sky-100 dark:bg-sky-900/30',
  },
  chat: {
    label: 'Chat',
    icon: MessageSquare,
    iconClass: 'text-violet-600 dark:text-violet-400',
    bgClass: 'bg-violet-100 dark:bg-violet-900/30',
  },
  web_form: {
    label: 'Web form',
    icon: Globe,
    iconClass: 'text-amber-600 dark:text-amber-400',
    bgClass: 'bg-amber-100 dark:bg-amber-900/30',
  },
}

// Fallback for any lead whose `source` doesn't match the canonical enum
// (e.g., legacy rows or affiliate-sandbox seed data using older labels
// like 'voice' / 'webhook'). Without this, SOURCE_META[unknown] is
// undefined and the row render throws, crashing the entire Leads page.
const UNKNOWN_SOURCE_META = {
  label: 'Other',
  icon: Users,
  iconClass: 'text-slate-600 dark:text-slate-400',
  bgClass: 'bg-slate-100 dark:bg-slate-800/40',
}
const UNKNOWN_STATUS_META = {
  label: 'Unknown',
  className: 'bg-slate-500/15 text-slate-700 dark:text-slate-300',
}

function scoreBadgeClass(score: number): string {
  // Canonical pill: bg-{color}-500/15 + dark:text-{color}-400 — matches
  // the status pills on Billing / Calls / Bookings instead of the older
  // raw bg-{color}-100 / text-{color}-700 pattern.
  if (score >= 70) return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
  if (score >= 40) return 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
  return 'bg-rose-500/15 text-rose-700 dark:text-rose-400'
}

const STATUS_META: Record<LeadStatus, { label: string; className: string }> = {
  new: { label: 'New', className: 'bg-brand-periwinkle/15 text-brand-royal dark:text-brand-periwinkle' },
  contacted: { label: 'Contacted', className: 'bg-slate-500/15 text-slate-700 dark:text-slate-300' },
  booked: { label: 'Booked', className: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' },
  qualified: { label: 'Qualified', className: 'bg-violet-500/15 text-violet-700 dark:text-violet-400' },
  won: { label: 'Won', className: 'bg-green-500/15 text-green-700 dark:text-green-400' },
  lost: { label: 'Lost', className: 'bg-rose-500/15 text-rose-700 dark:text-rose-400' },
}

function displayName(lead: Lead): string {
  return lead.name || lead.email || (lead.phone ? formatPhoneDisplay(lead.phone) : 'Unknown lead')
}

export default function LeadsPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [leads, setLeads] = useState<Lead[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  // Pre-fill search from `?q=…` so deep-links from other surfaces (e.g. the
  // Bookings Calendar context-menu's "View lead") jump straight to the match.
  const [search, setSearch] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('q') || '' } catch { return '' }
  })
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [sourceFilter, setSourceFilter] = useState<'all' | LeadSource>('all')
  const [statusFilter, setStatusFilter] = useState<'all' | LeadStatus>('all')

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams()
        if (sourceFilter !== 'all') params.set('source', sourceFilter)
        if (statusFilter !== 'all') params.set('status', statusFilter)
        if (debouncedSearch) params.set('q', debouncedSearch)
        params.set('limit', '100')
        const res = await api.get(`/api/leads?${params.toString()}`)
        if (!cancelled && res.data) {
          setLeads(res.data.leads || [])
          setTotal(res.data.total || 0)
        }
      } catch (err: any) {
        if (!cancelled) {
          toast({
            title: 'Failed to load leads',
            description: err?.response?.data?.message || 'Please try refreshing.',
            variant: 'destructive',
          })
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [debouncedSearch, sourceFilter, statusFilter, toast])

  return (
    <div className="pb-12">
      <PageHeader
        eyebrow="People"
        title="Leads"
        subtitle="Unified view of everyone who's contacted you — across calls, chats, and web forms."
        icon={
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-brand-royal/20 to-brand-periwinkle/15 border border-brand-royal/20 flex items-center justify-center">
            <Users className="h-5 w-5 text-brand-royal" />
          </div>
        }
        actions={
          <div className="text-xs text-muted-foreground tabular-nums">
            {loading ? 'Loading…' : `${total} lead${total === 1 ? '' : 's'}`}
          </div>
        }
      />

      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search name, email, phone…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={sourceFilter} onValueChange={v => setSourceFilter(v as any)}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="inbound_call">Inbound call</SelectItem>
            <SelectItem value="outbound_call">Outbound call</SelectItem>
            <SelectItem value="chat">Chat</SelectItem>
            <SelectItem value="web_form">Web form</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={v => setStatusFilter(v as any)}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="booked">Booked</SelectItem>
            <SelectItem value="qualified">Qualified</SelectItem>
            <SelectItem value="won">Won</SelectItem>
            <SelectItem value="lost">Lost</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading && leads.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : leads.length === 0 ? (
        <EmptyState
          icon={Users}
          tone="periwinkle"
          title="No leads yet"
          description="When someone contacts you via call, chat, or a web form, they'll show up here with their case type, score, and source."
        />
      ) : (
        <div className="border rounded-lg overflow-hidden bg-card">
          <div className="hidden md:grid grid-cols-[auto_1.5fr_1.4fr_1.1fr_1.1fr_0.8fr_0.9fr_1.1fr] gap-4 px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide border-b bg-muted/30">
            <div className="w-10"></div>
            <div>Name</div>
            <div>Email</div>
            <div>Phone</div>
            <div>Case type</div>
            <div>Score</div>
            <div>Status</div>
            <div>Last activity</div>
          </div>
          <div>
            {leads.map(lead => {
              const srcMeta = SOURCE_META[lead.source] ?? UNKNOWN_SOURCE_META
              const SrcIcon = srcMeta.icon
              const statusMeta = STATUS_META[lead.status] ?? UNKNOWN_STATUS_META
              const lastActivity = new Date(lead.lastActivityAt)
              return (
                <button
                  key={lead.id}
                  onClick={() => navigate(`/leads/${lead.id}`)}
                  className="w-full grid grid-cols-1 md:grid-cols-[auto_1.5fr_1.4fr_1.1fr_1.1fr_0.8fr_0.9fr_1.1fr] gap-2 md:gap-4 px-4 py-3 text-left border-b last:border-b-0 hover:bg-accent/40 transition-colors items-center"
                >
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${srcMeta.bgClass}`}>
                          <SrcIcon className={`w-4 h-4 ${srcMeta.iconClass}`} />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right">{srcMeta.label}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <div className="min-w-0">
                    <div className="font-medium text-foreground truncate">{displayName(lead)}</div>
                  </div>
                  <div className="min-w-0 text-sm text-muted-foreground">
                    {lead.email ? (
                      <span className="flex items-center gap-1.5 truncate">
                        <Mail className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{lead.email}</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground/50">—</span>
                    )}
                  </div>
                  <div className="min-w-0 text-sm text-muted-foreground">
                    {lead.phone ? (
                      <span className="flex items-center gap-1.5 truncate">
                        <Phone className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{formatPhoneDisplay(lead.phone)}</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground/50">—</span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground truncate">
                    {lead.caseType || <span className="text-muted-foreground/50">—</span>}
                  </div>
                  <div>
                    {lead.leadScore != null ? (
                      <Badge variant="secondary" className={scoreBadgeClass(lead.leadScore)}>
                        {lead.leadScore}
                      </Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground/50">—</span>
                    )}
                  </div>
                  <div>
                    <Badge variant="secondary" className={statusMeta.className}>
                      {statusMeta.label}
                    </Badge>
                  </div>
                  <div className="text-sm leading-tight">
                    <div className="text-foreground">
                      {formatDistanceToNow(lastActivity, { addSuffix: true })}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {format(lastActivity, 'MMM d, h:mm a')}
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
